import React, { FC, useEffect, useState } from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import ProgramsService from '../../services/programs.service';
import useService from '../../hooks/service.hook';
import { ProgramBilling, ProgramInvoiceListItem } from '../../models/billing.models';
import './program-billing.css';

export interface ProgramBillingHistoryProps {
  programId?: number;
  /** Every open plan. Invoices on a shared bill are listed once. */
  programIds?: number[];
  /** Change to reload the card and payment history, e.g. after a plan change. */
  refreshKey?: number;
}

const BRAND_SHORT: Record<string, string> = {
  visa: 'VISA',
  mastercard: 'MC',
  amex: 'AMEX',
  discover: 'DISC',
};

const brandName = (brand: string | null): string => {
  if (!brand) return 'Card';
  if (brand === 'mastercard') return 'Mastercard';
  if (brand === 'amex') return 'American Express';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
};

const INVOICE_STATUS: Record<string, { label: string; className: string }> = {
  paid: { label: 'Paid', className: 'pb-chip' },
  open: { label: 'Open', className: 'pb-chip pb-chip-warning' },
  uncollectible: { label: 'Failed', className: 'pb-chip pb-chip-danger' },
};

/** Card on file (view only) and payment history for the account bill. */
const ProgramBillingHistory: FC<ProgramBillingHistoryProps> = ({ programId, programIds, refreshKey = 0 }) => {
  const programsService = useService(ProgramsService);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingInfo, setBillingInfo] = useState<ProgramBilling | null>(null);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoices, setInvoices] = useState<ProgramInvoiceListItem[]>([]);
  const idKey = (programIds && programIds.length > 0 ? programIds : programId != null ? [programId] : []).join(',');

  useEffect(() => {
    setBillingLoading(true);
    setInvoicesLoading(true);
  }, [idKey]);

  // Refreshes keep the current rows on screen until the new data arrives.
  useEffect(() => {
    const ids = idKey
      ? idKey
          .split(',')
          .map((value) => Number(value))
          .filter((id) => !Number.isNaN(id))
      : [];
    if (ids.length === 0) {
      setBillingInfo(null);
      setInvoices([]);
      setBillingLoading(false);
      setInvoicesLoading(false);
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      ids.map((id) =>
        programsService
          .getBilling(id)
          .then((response) => response.data.data as ProgramBilling)
          .catch(() => null)
      )
    )
      .then((cards) => {
        if (cancelled) return;
        setBillingInfo(cards.find((card) => card?.brand || card?.last4) || null);
      })
      .finally(() => !cancelled && setBillingLoading(false));
    Promise.all(
      ids.map((id) =>
        programsService
          .getInvoices(id)
          .then((response) => (response.data.data || []) as ProgramInvoiceListItem[])
          .catch(() => [] as ProgramInvoiceListItem[])
      )
    )
      .then((lists) => {
        if (cancelled) return;
        const byId = new Map<string, ProgramInvoiceListItem>();
        lists.flat().forEach((invoice) => {
          if (!byId.has(invoice.id)) byId.set(invoice.id, invoice);
        });
        setInvoices(Array.from(byId.values()).sort((left, right) => right.date_issued.localeCompare(left.date_issued)));
      })
      .finally(() => !cancelled && setInvoicesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [idKey, refreshKey, programsService]);

  const hasCard = Boolean(billingInfo?.brand || billingInfo?.last4);

  return (
    <>
      <div className="pb-card">
        <div className="pb-cap">Payment Method</div>
        {billingLoading ? (
          <div className="pb-loading">
            <CircularProgress size={20} />
          </div>
        ) : (
          <div className="pb-pay">
            <span className="pb-brand">{hasCard ? BRAND_SHORT[billingInfo?.brand || ''] || 'CARD' : '—'}</span>
            <div>
              <div className="pb-pay-title">
                {hasCard
                  ? `${brandName(billingInfo?.brand || null)} •••• ${billingInfo?.last4 || ''}`
                  : 'No payment method on file'}
              </div>
              <div className="pb-pay-sub">
                {hasCard ? 'Used for every team on this bill' : 'Added when you convert to paid'}
              </div>
            </div>
            {hasCard ? (
              <Tooltip title="Contact support to update your payment method">
                <span className="pb-foot-end">
                  <button type="button" className="pb-link" disabled>
                    Update
                  </button>
                </span>
              </Tooltip>
            ) : null}
          </div>
        )}
      </div>

      <div className="pb-card">
        <div className="pb-cap">Payment History</div>
        {invoicesLoading ? (
          <div className="pb-loading">
            <CircularProgress size={20} />
          </div>
        ) : invoices.length === 0 ? (
          <div className="pb-empty">No payments yet</div>
        ) : (
          <div className="pb-table-wrap">
            <table className="pb-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th className="pb-right">Amount</th>
                  <th>Status</th>
                  <th className="pb-right">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  const status = INVOICE_STATUS[invoice.status] || {
                    label: invoice.status,
                    className: 'pb-chip pb-chip-muted',
                  };
                  return (
                    <tr key={invoice.id}>
                      <td className="pb-t-date">{dayjs(invoice.date_issued).format('MMM D, YYYY')}</td>
                      <td className="pb-t-desc">
                        <div>{invoice.description || invoice.month}</div>
                        <div className="pb-t-desc-sub">{invoice.number || invoice.id}</div>
                      </td>
                      <td className="pb-t-amount">{invoice.amount}</td>
                      <td>
                        <span className={status.className}>{status.label}</span>
                      </td>
                      <td className="pb-right">
                        {invoice.pdf ? (
                          <a className="pb-receipt" href={invoice.pdf} target="_blank" rel="noopener noreferrer">
                            Receipt
                          </a>
                        ) : (
                          <span className="pb-note">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default ProgramBillingHistory;
